import React from 'react';
import { Send, X, Ticket as TicketIcon, Image as ImageIcon } from 'lucide-react';
import type { TicketCreate } from '../../services/ticketService';

interface SupportTicketFormModalProps {
  open: boolean;
  formData: TicketCreate;
  setFormData: React.Dispatch<React.SetStateAction<TicketCreate>>;
  customIssueType: string;
  setCustomIssueType: (v: string) => void;
  imagePreviews: string[];
  imageNames: string[];
  getFilenameFromUrl: (url: string) => string;
  onImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveImage: (index: number) => void;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
  loading: boolean;
  uploadingImage: boolean;
}

export const SupportTicketFormModal: React.FC<SupportTicketFormModalProps> = ({
  open,
  formData,
  setFormData,
  customIssueType,
  setCustomIssueType,
  imagePreviews,
  imageNames,
  getFilenameFromUrl,
  onImageChange,
  onRemoveImage,
  onSubmit,
  onClose,
  loading,
  uploadingImage,
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 px-4">
      <div className="bg-slate-900/95 border border-gray-700/50 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto custom-scrollbar">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg shadow-lg">
                <TicketIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Create New Ticket</h2>
                <p className="text-xs text-gray-400">Submit a support request</p>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors flex-shrink-0">
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label htmlFor="modal-title" className="block text-xs font-medium text-gray-400 mb-1.5">Title</label>
              <input
                type="text"
                id="modal-title"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700/50 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all"
                placeholder="Brief summary"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="modal-issue_type" className="block text-xs font-medium text-gray-400 mb-1.5">Issue Type</label>
                <select
                  id="modal-issue_type"
                  required
                  value={formData.issue_type || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData({ ...formData, issue_type: value as any });
                    if (value !== 'other') setCustomIssueType('');
                  }}
                  className="w-full px-3 py-2 bg-gray-950 border border-gray-800 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all dark-select"
                >
                  <option value="" disabled className="bg-gray-950 text-white">Select Issue Type</option>
                  <option value="credits" className="bg-gray-950 text-white">Credits</option>
                  <option value="subscription" className="bg-gray-950 text-white">Subscription</option>
                  <option value="billing" className="bg-gray-950 text-white">Billing</option>
                  <option value="technical" className="bg-gray-950 text-white">Technical</option>
                  <option value="other" className="bg-gray-950 text-white">Other</option>
                </select>
                {formData.issue_type === 'other' && (
                  <div className="mt-2">
                    <input
                      type="text"
                      id="modal-custom-issue-type"
                      value={customIssueType}
                      onChange={(e) => setCustomIssueType(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700/50 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all"
                      placeholder="Please specify issue type (optional)"
                    />
                  </div>
                )}
              </div>
              <div>
                <label htmlFor="modal-priority" className="block text-xs font-medium text-gray-400 mb-1.5">Priority</label>
                <select
                  id="modal-priority"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                  className="w-full px-3 py-2 bg-gray-950 border border-gray-800 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all dark-select"
                >
                  <option value="low" className="bg-gray-950 text-white">Low</option>
                  <option value="medium" className="bg-gray-950 text-white">Medium</option>
                  <option value="high" className="bg-gray-950 text-white">High</option>
                  <option value="urgent" className="bg-gray-950 text-white">Urgent</option>
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="modal-description" className="block text-xs font-medium text-gray-400 mb-1.5">Description</label>
              <textarea
                id="modal-description"
                required
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700/50 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 resize-none transition-all"
                placeholder="Describe your issue..."
              />
            </div>
            <div>
              <label htmlFor="modal-ticket-image" className="block text-xs font-medium text-gray-400 mb-1.5">Attachments (Optional)</label>
              {imagePreviews.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {imagePreviews
                    .map((preview, originalIndex) => {
                      const isValid = preview.startsWith('data:') || preview.startsWith('http://') || preview.startsWith('https://') || preview.startsWith('/api/');
                      if (!isValid) return null;
                      const imageUrl = preview.startsWith('/api/') ? `${process.env.REACT_APP_API_URL || ''}${preview}` : preview;
                      const fileName = imageNames[originalIndex] || getFilenameFromUrl(imageUrl);
                      return (
                        <div key={originalIndex} className="relative inline-block">
                          <div className="relative">
                            <img
                              src={imageUrl}
                              alt={`Preview ${originalIndex + 1}`}
                              className="max-w-xs h-auto rounded-lg border border-gray-700/50 max-h-32 object-contain"
                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                            <button
                              type="button"
                              onClick={() => onRemoveImage(originalIndex)}
                              className="absolute top-1 right-1 p-1 bg-red-500/80 hover:bg-red-600 rounded-full text-white"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                          <p className="text-xs text-gray-400 mt-1 truncate max-w-[200px]" title={fileName}>{fileName}</p>
                        </div>
                      );
                    })
                    .filter(item => item !== null)}
                </div>
              )}
              <label
                htmlFor="modal-ticket-image"
                className="flex items-center justify-center w-full px-4 py-3 bg-gray-900/50 border border-gray-700/50 rounded-lg cursor-pointer hover:bg-gray-900/70 transition-colors"
              >
                <ImageIcon className="w-4 h-4 mr-2 text-gray-400" />
                <span className="text-sm text-gray-400">Choose attachments</span>
                <input
                  id="modal-ticket-image"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={onImageChange}
                  className="hidden"
                />
              </label>
            </div>
            <div className="flex items-center justify-end space-x-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || uploadingImage}
                className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm rounded-lg font-medium hover:from-cyan-600 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {(loading || uploadingImage) ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>{uploadingImage ? 'Uploading image...' : 'Creating...'}</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Create Ticket</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
